/**
 * Logging utility for consistent logging across the application
 * Provides environment-aware logging (silent in production if needed)
 */

class Logger {
  constructor() {
    // Check if we're in development mode (Shopify theme editor or localhost)
    this.isDevelopment = 
      typeof Shopify !== 'undefined' && Shopify.designMode === true ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('.myshopify.com');
  }

  /**
   * Log an error message
   * @param {string} message - Error message
   * @param {Error|Object} [error] - Error object or additional error data
   * @param {string} [context] - Context where the error occurred
   */
  error(message, error = null, context = '') {
    const logMessage = context ? `[${context}] ${message}` : message;
    
    if (error instanceof Error) {
      console.error(logMessage, error);
    } else if (error) {
      console.error(logMessage, error);
    } else {
      console.error(logMessage);
    }
    
    // In production, you could send errors to an error tracking service here
    // Example: if (window.Sentry) window.Sentry.captureException(error);
  }

  /**
   * Log a warning message
   * @param {string} message - Warning message
   * @param {Object} [data] - Additional data
   * @param {string} [context] - Context where the warning occurred
   */
  warn(message, data = null, context = '') {
    if (!this.isDevelopment) return;
    
    const logMessage = context ? `[${context}] ${message}` : message;
    if (data) {
      console.warn(logMessage, data);
    } else {
      console.warn(logMessage);
    }
  }

  /**
   * Log an info message
   * @param {string} message - Info message
   * @param {Object} [data] - Additional data
   * @param {string} [context] - Context
   */
  info(message, data = null, context = '') {
    if (!this.isDevelopment) return;
    
    const logMessage = context ? `[${context}] ${message}` : message;
    if (data) {
      console.log(logMessage, data);
    } else {
      console.log(logMessage);
    }
  }

  /**
   * Log a debug message (only in development)
   * @param {string} message - Debug message
   * @param {Object} [data] - Additional data
   * @param {string} [context] - Context
   */
  debug(message, data = null, context = '') {
    if (!this.isDevelopment) return;
    
    const logMessage = context ? `[${context}] ${message}` : message;
    if (data) {
      console.debug(logMessage, data);
    } else {
      console.debug(logMessage);
    }
  }
}

// Create a singleton instance
const logger = new Logger();

// Export for use in modules or global scope
if (typeof window !== 'undefined') {
  window.logger = logger;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = logger;
}

